import { useEffect, useMemo, useState } from "react";
import { SuccessMessage, TaxRefund } from "src/components";
import styles from "./styles.module.scss";
import {
  // Remove setIsLoading import
} from "src/redux/slices/appSlice/AppSlice";
import { useAppDispatch, useAppSelector } from "src/hooks/useRedux";
import { useIsDigitalOrDigitalAgent } from "src/hooks/useIsDigitalOrDigitalAgent";
import { API_RESULT, ErrorResponseCode } from "src/utils/enums";
import { images } from "src/assets/images/images";
import {
  __DEV__,
  getTaxRefundCondition,
  sentPostMessage,
} from "src/utils/utils";
import {
  TAX_REFUND_CONDITION,
} from "src/utils/types";
import { getCountries, getTaxRefundInfo } from "src/redux/slices/userSlice/UserApi";

const TaxRefundIframe: React.FC = () => {
  const dispatch = useAppDispatch();

  const [showError, setShowError] = useState(false);
  // Add local loading state
  const [isLoading, setIsLoading] = useState(false);
  const {
    hostUrl,
    strings,
  } = useAppSelector((state) => state.app);
  const isDigitalOrAgent = useIsDigitalOrDigitalAgent();
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessageText, setErrorMessageText] = useState("");
  const [errorMessageSubtitle, setErrorMessageSubtitle] = useState("");
  const { taxRefundInfoResStatus, taxRefundInfoRes, taxRefundInfoError } =
    useAppSelector((state) => state.user);

  const taxRefundCondition = useMemo(() => {
    if (!taxRefundInfoRes) return TAX_REFUND_CONDITION.NOT_ELIGIBLE;

    return getTaxRefundCondition({
      isRegistered: taxRefundInfoRes?.data?.feCustTaxRefundRegistration,
      isEligible: taxRefundInfoRes?.data?.feCustaxRefundEligibility,
    });
  }, [taxRefundInfoRes]);

  useEffect(() => {
    // Using local state instead of Redux
    setIsLoading(true);
    const handleMessage = (event: MessageEvent) => {
      try {
        const {
          personalId,
          businessUnit,
          guid,
          travelPolicyNumber,
          agentId,
          salesExperimentID,
          experimentName,
          salesExperimentVersionID,
          versionName,
          experimentType,
        } = event.data;

        if (personalId && businessUnit && guid) {
          dispatch(getTaxRefundInfo({
            feCustTravalPolicyNumber: travelPolicyNumber ?? "",
            feGuid: guid,
            feCustPersId: personalId,
            feBU: businessUnit,
          }))

        }
      } catch (error) {
        __DEV__ && console.error("Error processing message:", error);
        // Handle error in local state
        setIsLoading(false);
      }
    };

    if (hostUrl && isDigitalOrAgent) {
      window.addEventListener("message", handleMessage);
    }
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [hostUrl, isDigitalOrAgent]);

  const errorMessage = useMemo(() => {
    switch (taxRefundCondition) {
      case TAX_REFUND_CONDITION.ELIGIBLE_NOT_REGISTERED:
        return strings.t_global_blue_sub_title_1;
      case TAX_REFUND_CONDITION.ELIGIBLE_AND_REGISTERED:
        return strings.t_global_blue_sub_title_2;
      case TAX_REFUND_CONDITION.REGISTERED_NOT_ELIGIBLE:
        return strings.t_global_blue_sub_title_3;
    }
  }, [taxRefundCondition, strings]);

  useEffect(() => {
    if (taxRefundInfoResStatus === API_RESULT.SUCCEEDED) {
      dispatch(getCountries({}));
      // response code
      if (taxRefundInfoRes?.data.feCustTaxRefundRegistration === true) {
        // Using local state instead of Redux
        setIsLoading(false);
        setShowError(true);
        setErrorMessageText(strings.t_tax_refund_general_error_title);
        setErrorMessageSubtitle(strings.t_global_blue_already_registered || "");
      } else if (
        taxRefundInfoRes?.data.feCustaxRefundEligibility === false
      ) {
        // Using local state instead of Redux
        setIsLoading(false);
        setShowError(true);
        setErrorMessageText(strings.t_tax_refund_general_error_title);
        setErrorMessageSubtitle(
          strings.t_global_blue_not_supported_country || ""
        );
      } else if (
        taxRefundInfoRes?.data.feCustaxRefundEligibility === true
      ) {
        // Using local state instead of Redux
        setIsLoading(false);
      }
    } else if (taxRefundInfoResStatus === API_RESULT.FAILED) {
      setShowError(true);
      setErrorMessageText(strings.t_tax_refund_general_error_title);
      setErrorMessageSubtitle(strings.t_tax_refund_general_error_message);
      // Using local state instead of Redux
      setIsLoading(false);
      if (taxRefundInfoRes?.responseCode != "0") {
        // Using local state instead of Redux
        setIsLoading(false);
        setShowError(true);
        if (isDigitalOrAgent) {
          setErrorMessageText(strings.generalErrorIframeSessionExpired);
          setErrorMessageSubtitle(errorMessage || strings.generalErrorIframeContinueViaApp);
        } else {
          setErrorMessageText(strings.t_tax_refund_general_error_title);
          setErrorMessageSubtitle(strings.t_tax_refund_general_error_message);
        }
        //  const info = {
        //    errorMessage: taxRefundInfoRes?.responseMessage  || taxRefundInfoError?.data?.responseData?.responseMessage || "403 Forbidden or 401 Unauthorized",
        //    responseCode:
        //      taxRefundInfoRes?.responseCode?.toString() || taxRefundInfoError?.data?.responseData?.responseCode?.toString() || ErrorResponseCode.GENERAL_ERROR.toString(),
        //    status: API_RESULT.FAILED,
        //  };
        //  sentPostMessage(info);
      }
    }
  }, [taxRefundInfoResStatus]);

  const onSubmit = (data: API_RESULT) => {
    __DEV__ && console.log("onSubmit data==>", data);
    if (data === API_RESULT.SUCCEEDED) {
      setShowSuccess(true);
    } else {
      setShowError(true);
      setErrorMessageText(strings.t_tax_refund_general_error_title);
      setErrorMessageSubtitle(strings.t_tax_refund_general_error_message);
    }
  };

  return (
    <div className={`${styles.container} ${isDigitalOrAgent ? "" : styles.padding}`}>
      {isLoading ? (
        <div className={styles.loadingWrapper}>
          <div className={styles.loader}></div>
        </div>
      ) : showSuccess || showError ? (
        <div className={styles.successWrapper}>
          <SuccessMessage
            title={
              showSuccess
                ? strings.t_global_blue_success_title
                : errorMessageText
            }
            subtitle={
              showSuccess
                ? strings.t_global_blue_success_subtitle
                : errorMessageSubtitle
            }
            image={
              showSuccess ? images.globalBlueSuccess : images.globalBlueError
            }
            imageAlt="Success"
          />
        </div>
      ) : (
        <TaxRefund onSubmitStatus={onSubmit} />
      )}
    </div>
  );
};

export default TaxRefundIframe;
